import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import i18n from 'i18n-js';
import { Box, Grid } from '@material-ui/core';
import { BaseModal } from 'components/UI';
import { useStyles } from './creditTypes.styled';
import { Formik, Form } from 'formik';
import { BasicInformationInputs, initialValues, formSchema } from 'components/CreditTypes';
import { creditTypesActions } from 'state/modules/creditTypes';
import { PricingActions } from 'state/modules/pricing';
import { FETCH_STATE } from 'constants';
import { parseInputNumber } from 'utils';

const CreateCreditTypeModal = ({ id, clone, open, toggleModal }) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const formRef = useRef(null);
  // Ger accounting data
  const accounting = useSelector((state) => state?.pricing?.accountingTypes);
  const fetchingAccountingTypes = useSelector((state) => state?.pricing?.fetchingAccountingTypes);
  const creditType = useSelector((state) => state?.creditTypes?.creditType);
  const createCreditType = useSelector((state) => state?.creditTypes?.createCreditType);
  const updateCreditType = useSelector((state) => state?.creditTypes?.updateCreditType);

  const init = useMemo(() => initialValues(id && creditType, clone), [id, creditType, clone]);
  const validationSchema = useMemo(() => formSchema(), []);

  const onCancel = useCallback(() => {
    toggleModal();
    //    dispatch(Credit_typesActions.resetUpdateReservation());
  }, [dispatch, toggleModal]);

  /**
   * Handle form submit
   * @param {object} params Object with the value of fields of the form
   */
  const onSubmit = (params) => {
    const payload = {
      ...params,
      default_unit_price: parseInputNumber(params.default_unit_price),
    };
    if (id) {
      dispatch(creditTypesActions.updateCreditType(id, payload));
    } else {
      dispatch(
        creditTypesActions.createCreditType({ ...payload, accounting_type_id: accounting?.[0]?.id })
      );
    }
  };

  const handleSubmit = () => {
    formRef.current.submitForm();
  };

  // Load accounting types on component load
  useEffect(() => {
    dispatch(PricingActions.fetchAccountingTypes({ is_account_level: true }));
  }, []);

  // Close modal un success update or create and reset create update state
  useEffect(() => {
    if (createCreditType === FETCH_STATE.DONE || updateCreditType === FETCH_STATE.DONE) {
      toggleModal();
      dispatch(creditTypesActions.updateCreditTypeReset());
    }
  }, [dispatch, createCreditType, updateCreditType]);

  return (
    <BaseModal
      size="md"
      open={open}
      hasEnterButton
      hasCancelButton
      onEnter={handleSubmit}
      onCancel={onCancel}
      closeButtonType="icon"
      enableCloseButton={false}
      title={
        id ? i18n.t('credit_types.update_credit_type') : i18n.t('credit_types.create_credit_type')
      }
      enterButtonLabel={id ? i18n.t('common.update') : i18n.t('common.create')}
      maxWidth={680}
      disabled={
        createCreditType === FETCH_STATE.LOADING ||
        updateCreditType === FETCH_STATE.LOADING ||
        accounting?.length === 0 ||
        fetchingAccountingTypes
      }
    >
      <Grid container direction="column" alignItems="flex-start" data-cy="create-credit-type-modal">
        <Box width="100%">
          <Formik
            initialValues={init}
            validationSchema={validationSchema}
            enableReinitialize={true}
            innerRef={formRef}
            onSubmit={onSubmit}
            validateOnChange={false}
            validateOnBlur={false}
          >
            {({ handleSubmit, errors, values }) => {
              return (
                <Form noValidate onSubmit={handleSubmit}>
                  <BasicInformationInputs errors={errors} values={values} />
                </Form>
              );
            }}
          </Formik>
        </Box>
        <Box>
          {accounting?.length === 0 && !fetchingAccountingTypes && (
            <Box className={classes.error} mt={2}>
              <span
                dangerouslySetInnerHTML={{ __html: i18n.t('credit_types.no_accounting_types') }}
              />
            </Box>
          )}
        </Box>
      </Grid>
    </BaseModal>
  );
};

export { CreateCreditTypeModal };
